import React, { useState, useEffect } from 'react';
import Hero from '../../components/Hero/Hero';
import productAPI from '../../services/productApi';
import './Home.css';

const Home = ({ setCurrentPage, setPageData }) => {
  const [featuredServices, setFeaturedServices] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [servicesError, setServicesError] = useState(null);
  const [productsError, setProductsError] = useState(null);

  // Helper function to format price as dollar
  const formatDollarPrice = (price) => {
    if (!price && price !== 0) return 'Contact for Quote';
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return 'Contact for Quote';
    return `$${numPrice.toFixed(2)}`;
  };

  // Icon mapping for services based on name/category
  const getServiceIcon = (serviceName, categoryName = '') => {
    const name = (serviceName + ' ' + categoryName).toLowerCase();
    
    const iconMap = {
      // Camera related
      'camera': '🎥',
      'surveillance': '🎥',
      'cctv': '🎥',
      'video': '🎥',
      'security camera': '🎥',
      'ip camera': '🎥',
      
      // NVR/Recording related
      'nvr': '🖥️',
      'recording': '🖥️',
      'recorder': '🖥️',
      'dvr': '🖥️',
      'storage': '💾',
      'system setup': '🖥️',
      
      // Phone/Communication related
      'phone': '☎️',
      'voip': '☎️',
      'communication': '☎️',
      'telephony': '☎️',
      'pbx': '☎️',
      
      // Access control
      'access': '🔒',
      'control': '🔒',
      'door': '🔒',
      'keypad': '🔒',
      
      // Installation/Service
      'installation': '🔧',
      'setup': '🔧',
      'install': '🔧',
      'mounting': '🔧',
      
      // Monitoring
      'monitoring': '👁️',
      'watch': '👁️',
      'guard': '👁️',
      
      // Networking
      'network': '🌐',
      'wireless': '📶',
      'wifi': '📶',
      
      // Maintenance
      'maintenance': '⚙️',
      'repair': '⚙️',
      'service': '⚙️'
    };

    // Find matching icon
    for (const [key, icon] of Object.entries(iconMap)) {
      if (name.includes(key)) {
        return icon;
      }
    }

    // Default icon
    return '🔧';
  };

  // Navigation handlers
  const handleServiceClick = (serviceId) => {
    setPageData({
      itemId: serviceId,
      itemType: 'service'
    });
    setCurrentPage('details');
  };

  const handleProductClick = (productId) => {
    setPageData({
      itemId: productId,
      itemType: 'product'
    });
    setCurrentPage('details');
  };

  // Load featured services from API
  const loadFeaturedServices = async () => {
    try {
      setServicesLoading(true);
      setServicesError(null);

      // Try featured services first, fallback to regular services
      let response;
      try {
        response = await productAPI.getServices({
          featured_only: true,
          per_page: 3,
          page: 1
        });
      } catch (featuredError) {
        console.log('Featured services not available, trying regular services:', featuredError);
        response = await productAPI.getServices({
          per_page: 3,
          page: 1
        });
      }

      if (response && response.success && response.data) {
        const services = response.data.services || [];
        
        if (services.length === 0) {
          throw new Error('No services available');
        }

        console.log('Loaded services:', services);

        // Transform API data to component format
        const transformedServices = services.slice(0, 3).map(service => {
          // Get category name
          const categoryName = service.category && typeof service.category === 'object' 
            ? service.category.name 
            : '';

          // Calculate pricing
          const isOnSale = service.is_on_sale && service.sale_price && service.sale_price < service.price;
          const currentPrice = isOnSale ? service.sale_price : service.price;
          
          return {
            id: service.id,
            title: service.name || service.title,
            description: service.description || service.short_description || 'Professional service',
            icon: getServiceIcon(service.name || service.title, categoryName),
            price: isOnSale 
              ? `Sale: ${formatDollarPrice(currentPrice)}` 
              : service.price 
                ? `Starting at ${formatDollarPrice(currentPrice)}`
                : 'Contact for Quote',
            originalPrice: isOnSale ? formatDollarPrice(service.price) : null,
            isOnSale: isOnSale,
            discount: isOnSale ? productAPI.calculateDiscount(service.price, service.sale_price) : null,
            category: categoryName || 'Service'
          };
        });

        setFeaturedServices(transformedServices);
      } else {
        throw new Error(response?.message || 'Failed to load services');
      }
    } catch (error) {
      console.error('Error loading featured services:', error);
      const errorInfo = productAPI.handleAPIError(error);
      setServicesError(errorInfo.message);
      
      // Set fallback services
      const fallbackServices = [
        {
          id: 'fallback-1',
          title: 'IP Camera Installation',
          description: 'High-definition security cameras with night vision and motion detection',
          icon: '🎥',
          price: 'Starting at $299.99'
        },
        {
          id: 'fallback-2',
          title: 'NVR System Setup',
          description: 'Network Video Recorders for centralized surveillance management',
          icon: '🖥️',
          price: 'Starting at $499.99'
        },
        {
          id: 'fallback-3',
          title: 'VoIP Phone Systems',
          description: 'Professional communication systems for business connectivity',
          icon: '☎️',
          price: 'Starting at $199.99'
        }
      ];
      
      setFeaturedServices(fallbackServices);
    } finally {
      setServicesLoading(false);
    }
  };

  // Load featured products from API
  const loadFeaturedProducts = async () => {
    try {
      setProductsLoading(true);
      setProductsError(null);

      // Try featured products first, fallback to regular products
      let response;
      try {
        response = await productAPI.getProducts({
          featured_only: true,
          per_page: 3,
          page: 1
        });
      } catch (featuredError) {
        console.log('Featured products not available, trying regular products:', featuredError);
        response = await productAPI.getProducts({
          per_page: 3,
          page: 1
        });
      }

      if (response && response.success && response.data) {
        const products = response.data.products || [];
        
        if (products.length === 0) {
          throw new Error('No products available');
        }

        console.log('Loaded products:', products);

        // Transform API data to component format
        const transformedProducts = products.slice(0, 3).map(product => {
          // Get category name
          const categoryName = product.category && typeof product.category === 'object' 
            ? product.category.name 
            : '';

          // Get image URL
          let imageUrl = null;
          if (product.images && Array.isArray(product.images) && product.images.length > 0) {
            imageUrl = product.images[0];
          } else if (product.primary_image) {
            imageUrl = product.primary_image;
          }

          // Calculate pricing
          const isOnSale = product.is_on_sale && product.sale_price && product.sale_price < product.price;
          const currentPrice = isOnSale ? product.sale_price : product.price;
          const discount = isOnSale ? productAPI.calculateDiscount(product.price, product.sale_price) : null;

          // Check stock status
          let inStock = true;
          if (product.track_inventory) {
            inStock = product.stock_quantity > 0;
          }

          return {
            id: product.id,
            title: product.name || product.title,
            description: product.description || product.short_description || 'Professional security equipment',
            image: imageUrl,
            price: formatDollarPrice(currentPrice),
            originalPrice: isOnSale ? formatDollarPrice(product.price) : null,
            isOnSale: isOnSale,
            discount: discount,
            category: categoryName || 'Product',
            inStock: inStock
          };
        });

        setFeaturedProducts(transformedProducts);
      } else {
        throw new Error(response?.message || 'Failed to load products');
      }
    } catch (error) {
      console.error('Error loading featured products:', error);
      const errorInfo = productAPI.handleAPIError(error);
      setProductsError(errorInfo.message);
      
      // Set fallback products
      const fallbackProducts = [
        {
          id: 'fallback-1',
          title: '4K Security Camera',
          description: 'Professional-grade 4K IP camera with infrared night vision',
          image: null,
          price: '$449.99',
          inStock: true
        },
        {
          id: 'fallback-2',
          title: '16-Channel NVR',
          description: 'Network Video Recorder supporting up to 16 camera channels',
          image: null,
          price: '$699.99',
          inStock: true
        },
        {
          id: 'fallback-3',
          title: 'VoIP Desktop Phone',
          description: 'Enterprise-grade VoIP phone with HD voice quality',
          image: null,
          price: '$189.99',
          inStock: true
        }
      ];
      
      setFeaturedProducts(fallbackProducts);
    } finally {
      setProductsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Check if productAPI is available
        if (!productAPI) {
          throw new Error('API not initialized');
        }
        
        // Load both services and products
        await Promise.all([
          loadFeaturedServices(),
          loadFeaturedProducts()
        ]);
        
      } catch (error) {
        console.error('Failed to load initial data:', error);
        
        // Ensure fallback data is set
        if (featuredServices.length === 0) {
          setFeaturedServices([
            {
              id: 'fallback-1',
              title: 'IP Camera Installation',
              description: 'High-definition security cameras with night vision and motion detection',
              icon: '🎥',
              price: 'Starting at $299.99'
            },
            {
              id: 'fallback-2',
              title: 'NVR System Setup',
              description: 'Network Video Recorders for centralized surveillance management',
              icon: '🖥️',
              price: 'Starting at $499.99'
            },
            {
              id: 'fallback-3',
              title: 'VoIP Phone Systems',
              description: 'Professional communication systems for business connectivity',
              icon: '☎️',
              price: 'Starting at $199.99'
            }
          ]);
        }

        if (featuredProducts.length === 0) {
          setFeaturedProducts([
            {
              id: 'fallback-1',
              title: '4K Security Camera',
              description: 'Professional-grade 4K IP camera with infrared night vision',
              image: null,
              price: '$449.99',
              inStock: true
            },
            {
              id: 'fallback-2',
              title: '16-Channel NVR',
              description: 'Network Video Recorder supporting up to 16 camera channels',
              image: null,
              price: '$699.99',
              inStock: true
            },
            {
              id: 'fallback-3',
              title: 'VoIP Desktop Phone',
              description: 'Enterprise-grade VoIP phone with HD voice quality',
              image: null,
              price: '$189.99',
              inStock: true
            }
          ]);
        }

        setServicesLoading(false);
        setProductsLoading(false);
      }
    };

    loadData();
  }, []);

  // Loading skeleton component
  const LoadingSkeleton = ({ type = 'service' }) => (
    <div className={`${type}-card skeleton`}>
      {type === 'service' ? (
        <>
          <div className="skeleton-icon"></div>
          <div className="skeleton-title"></div>
          <div className="skeleton-description"></div>
          <div className="skeleton-price"></div>
          <div className="skeleton-button"></div>
        </>
      ) : (
        <>
          <div className="skeleton-image"></div>
          <div className="skeleton-content">
            <div className="skeleton-title"></div>
            <div className="skeleton-description"></div>
            <div className="skeleton-footer">
              <div className="skeleton-price"></div>
              <div className="skeleton-button"></div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // Error component
  const ErrorMessage = ({ message, onRetry, type }) => (
    <div className="error-message">
      <div className="error-content">
        <h4>⚠️ Unable to load {type}</h4>
        <p>{message}</p>
        <button className="btn btn-sm btn-primary" onClick={onRetry}>
          Try Again
        </button>
      </div>
    </div>
  );

  const renderServiceIcon = (iconName) => {
    const strokeWidth = 2;
    const size = 28;
    
    switch (iconName) {
      case '🎥': // Camera/Surveillance
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="16" height="10" rx="2" ry="2" />
            <path d="M18 8l4-3v8l-4-3" />
            <path d="M2 17h16" />
          </svg>
        );
      case '🖥️': // NVR/Recorder
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="14" rx="2" ry="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
          </svg>
        );
      case '💾': // Storage/NVR HDD
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <polyline points="17 21 17 13 7 13 7 21" />
            <polyline points="7 3 7 8 15 8" />
          </svg>
        );
      case '☎️': // VoIP Phone
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
        );
      case '🔒': // Access Control
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        );
      case '👁️': // Remote Monitoring
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        );
      case '🌐': // Networking
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
        );
      case '⚙️': // Maintenance
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        );
      case '🔧': // Setup/Default
      default:
        return (
          <svg viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        );
    }
  };

  return (
    <div className="home-page">
      <Hero setCurrentPage={setCurrentPage} />
      
      {/* Featured Services Section */}
      <section className="section featured-services">
        <div className="container">
          <h2 className="section-title">Our Professional Services</h2>
          <p className="section-subtitle">
            Expert surveillance and communication solutions delivered by certified technicians
          </p>
          
          <div className="grid-3">
            {servicesLoading ? (
              // Loading skeletons
              Array(3).fill(0).map((_, index) => (
                <LoadingSkeleton key={`service-skeleton-${index}`} type="service" />
              ))
            ) : servicesError ? (
              <div className="grid-span-3">
                <ErrorMessage 
                  message={servicesError} 
                  onRetry={loadFeaturedServices}
                  type="services"
                />
              </div>
            ) : (
              featuredServices.map(service => (
                <div key={service.id} className="service-card fade-in">
                  <div className="service-icon">{renderServiceIcon(service.icon)}</div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  <div className="service-price">
                    {service.isOnSale && service.originalPrice && (
                      <span className="original-price">{service.originalPrice}</span>
                    )}
                    <span className={service.isOnSale ? 'sale-price' : ''}>{service.price}</span>
                    {service.isOnSale && service.discount && (
                      <span className="sale-badge">-{service.discount}% OFF</span>
                    )}
                  </div>
                  <button 
                    className="btn btn-primary service-btn"
                    onClick={() => handleServiceClick(service.id)}
                  >
                    Learn More
                  </button>
                </div>
              ))
            )}
          </div>
          
          <div className="section-cta">
            <button 
              className="btn btn-secondary"
              onClick={() => setCurrentPage('services')}
            >
              View All Services
            </button>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="section featured-products">
        <div className="container">
          <h2 className="section-title">Security Equipment</h2>
          <p className="section-subtitle">
            Professional-grade surveillance equipment from trusted manufacturers
          </p>
          
          <div className="grid-3">
            {productsLoading ? (
              // Loading skeletons
              Array(3).fill(0).map((_, index) => (
                <LoadingSkeleton key={`product-skeleton-${index}`} type="product" />
              ))
            ) : productsError ? (
              <div className="grid-span-3">
                <ErrorMessage 
                  message={productsError} 
                  onRetry={loadFeaturedProducts}
                  type="products"
                />
              </div>
            ) : (
              featuredProducts.map(product => (
                <div key={product.id} className="product-card fade-in">
                  <div className="product-image">
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt={product.title}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    {/* Always have placeholder ready for fallback */}
                    <div className="no-image-placeholder" style={{ display: product.image ? 'none' : 'flex' }}>
                      <div className="no-image-icon">📷</div>
                      <div className="no-image-text">No Image Available</div>
                    </div>
                    <div className="product-overlay">
                      <button 
                        className="btn btn-primary"
                        onClick={() => handleProductClick(product.id)}
                      >
                        Quick View
                      </button>
                    </div>
                    {product.isOnSale && product.discount && (
                      <div className="discount-badge">-{product.discount}%</div>
                    )}
                    {!product.inStock && (
                      <div className="stock-badge out-of-stock">Out of Stock</div>
                    )}
                  </div>
                  <div className="product-info">
                    <h3 className="product-title">{product.title}</h3>
                    <p className="product-description">{product.description}</p>
                    <div className="product-footer">
                      <div className="price-container">
                        {product.isOnSale && product.originalPrice && (
                          <span className="original-price">{product.originalPrice}</span>
                        )}
                        <span className={`product-price ${product.isOnSale ? 'sale-price' : ''}`}>
                          {product.price}
                        </span>
                      </div>
                      <button 
                        className="btn btn-primary"
                        disabled={!product.inStock}
                        onClick={() => handleProductClick(product.id)}
                      >
                        {product.inStock ? 'View Details' : 'Out of Stock'}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="section-cta">
            <button 
              className="btn btn-secondary"
              onClick={() => setCurrentPage('products')}
            >
              Browse All Equipment
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section why-choose-us">
        <div className="container">
          <h2 className="section-title">Why Choose Transcontinental Network?</h2>
          
          <div className="grid-4">
            <div className="feature-card slide-in-left">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <polyline points="17 11 19 13 23 9" />
                </svg>
              </div>
              <h3>Certified Technicians</h3>
              <p>Licensed security system specialists</p>
            </div>
            
            <div className="feature-card slide-in-left">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h3>24/7 Monitoring</h3>
              <p>Round-the-clock technical support</p>
            </div>
            
            <div className="feature-card slide-in-right">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              </div>
              <h3>Remote Access</h3>
              <p>Monitor from anywhere, anytime</p>
            </div>
            
            <div className="feature-card slide-in-right">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h3>Quality Guarantee</h3>
              <p>Professional installation warranty</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;